<template>
  <div class="paginator-container">
    <div class="results-text">
      {{ resultsText }}
    </div>
    <div class="paginator-wrapper">
      <button 
        :disabled="pageNumber === 1"
        class="primary"
        @click="changePage(1)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="10"
          viewBox="0 0 8 12"
          focusable="false"
          aria-hidden="true"
          role="none"
        ><path d="M5.874.35a1.28 1.28 0 011.761 0 1.165 1.165 0 010 1.695L3.522 6l4.113 3.955a1.165 1.165 0 010 1.694 1.28 1.28 0 01-1.76 0L0 6 5.874.35z" /></svg>
        First
      </button>
      <button
        v-for="(visiblePageNumber, index) in visiblePageNumbers"
        :key="index"
        class="secondary"
        :class="{current: pageNumber === visiblePageNumber}"
        :disabled="pageNumber === visiblePageNumber"
        @click="changePage(visiblePageNumber)"
      >
        {{ visiblePageNumber }}
      </button>
      <button
        :disabled="pageNumber === lastPageNumber"
        class="primary"
        @click="changePage(lastPageNumber)"
      >
        Last
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="10"
          viewBox="0 0 8 12"
          focusable="false"
          aria-hidden="true"
          role="none"
        ><path d="M2.126.35a1.28 1.28 0 00-1.761 0 1.165 1.165 0 000 1.695L4.478 6 .365 9.955a1.165 1.165 0 000 1.694 1.28 1.28 0 001.76 0L8 6 2.126.35z" /></svg>
      </button>
    </div>
    <div class="items-per-page">
      <span>Items Per Page</span>
      <Dropdown
        v-model="itemsPerPage"
        class="items-per-page-dropdown"
        :options="dropdownOptions"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Dropdown from '@/components/shared/Dropdown.vue'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'Paginator',
  components: {
    Dropdown
  },
  props: {
    totalResults: {
      type: Number,
      required: true
    },
    pageSize: {
      type: Number,
      required: true
    },
    pageNumber: {
      type: Number,
      required: true
    }
  },
  emits: ['pageChange', 'page-size-change'],
  data() {
    const dropdownOptions = [
      new DropdownOptionResponse(10, true, '10'),
      new DropdownOptionResponse(25, true, '25'),
      new DropdownOptionResponse(50, true, '50')
    ]

    return {
      dropdownOptions,
      itemsPerPage: this.pageSize
    }
  },
  computed: {
    lastPageNumber(): number {
      if (this.totalResults === 0) return 1
      return Math.ceil(this.totalResults / this.pageSize)
    },
    resultsText(): string {
      const firstResultNumber = this.totalResults === 0 ? 0 : ((this.pageNumber - 1) * this.pageSize) + 1
      const maximumPossibleResultNumber = this.pageNumber * this.pageSize
      const endingResultNumber = maximumPossibleResultNumber > this.totalResults ? this.totalResults : maximumPossibleResultNumber
      return `Results ${firstResultNumber} - ${endingResultNumber} of ${this.totalResults}`
    },
    visiblePageNumbers(): number[] {
      if (this.pageNumber >= 1 && this.pageNumber <= this.lastPageNumber) {
        let numbers = [this.pageNumber]

        if (this.pageNumber === 1) {
          if (this.totalResults > this.pageSize)
            numbers.push(2)
          if (this.totalResults > (this.pageSize * 2))
            numbers.push(3)
        } else if (this.pageNumber === this.lastPageNumber) {
          if (this.totalResults > this.pageSize)
            numbers.push(this.lastPageNumber - 1)
          if (this.totalResults > (this.pageSize * 2))
            numbers.push(this.lastPageNumber - 2)
        } else {
          numbers.push(this.pageNumber - 1)
          numbers.push(this.pageNumber + 1)
        }

        numbers = numbers.sort((a, b) => a - b).filter((elem, index, arr) => arr.indexOf(elem) === index)
        
        return numbers
      } else {
        this.pageNumber < 1 ? this.changePage(1) : this.changePage(this.lastPageNumber)
        return []
      }
    }
  },
  watch: {
    itemsPerPage(currentValue: number, oldValue: number): void {
      if (currentValue !== oldValue)
        this.$emit('page-size-change', this.itemsPerPage)
    }
  },
  methods: {
    changePage(selectedPage: number): void {
      this.$emit('pageChange', selectedPage)
    }
  }
})

</script>

<style scoped lang="scss">
.paginator-container {
  display: grid;
  grid-template-rows: repeat(3, 1fr);
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 15px;

  @include desktop {
    grid-template-rows: 1fr;
    grid-template-columns: 300px 1fr 300px;
  }

  > .results-text {
    grid-row: 1;
    grid-column: span 4;
    font-size: 14px;
    line-height: 41px;
    color: $Color-font-medium;
    font-weight: 600;
    text-align: center;

    @include desktop {
      grid-column: 1;
      justify-content: start;
      text-align: left;
    }
  }

  > .paginator-wrapper {
    grid-row: 3;
    grid-column: span 4;
    margin: auto;
    display: flex;
    justify-content: space-between;

    @include desktop {
      grid-row: 1;
      grid-column: 2;
    }

    > .primary {
      @include positive-button;
      padding: 10px 12px;
      line-height: 14px;
      min-width: unset;
      margin-right: 5px;

      &:disabled > svg {
        fill: rgba(0, 0, 0, 0.6)
      }

      &:last-of-type {
        margin-right: 0;
      }
    }

    > .secondary {
      @include secondary-button;
      padding: 10px 12px;
      color: $Color-sac-orange;
      line-height: 12px;
      min-width: unset;
      margin-right: 5px;

      &.current {
        border: 1px solid transparent;
        color: $Color-font-high;
      }
    }
  }

  > .items-per-page {
    grid-row: 2;
    grid-column: span 4;
    justify-content: center;
    display: flex;
    padding-top: 1px;

    @include desktop {
      grid-row: 1;
      grid-column: 3;
      justify-content: flex-end;
    }

    > span {
      font-size: 14px;
      line-height: 38px;
      color: $Color-font-medium;
      font-weight: 600;
    }

    > .items-per-page-dropdown {
      margin: auto 0 auto 10px;
    }
  }
}
</style>
